import { useCallback } from 'react';
import {
  normalizeAngle,
  snapAngle,
  angleFromPointerOnCircle,
} from '../../Editor/CompassWidget';

/**
 * BaseSlopeControls — Grundgefälle-Eingabe (#624, Epic #623).
 *
 * Eine globale Neigungsebene pro Garten: Richtung (Canvas-Winkel, in die das
 * Gelände ABFÄLLT) + Gesamtgefälle in cm. Kein Fabric-Canvas nötig — funktioniert
 * identisch auf Mobile. Wird in Wizard Step 4 und (Phase 2, #625) in den
 * Editor-Einstellungen verwendet.
 *
 * Winkel-Konvention wie CompassWidget/northAngle: 0° = oben, 90° = rechts,
 * im Uhrzeigersinn. Ist northAngle gesetzt, zeigen Presets/Labels
 * Himmelsrichtungen, sonst plan-relative Richtungen („unten rechts").
 */

// ---------------------------------------------------------------------------
// Konstanten + pure Helfer (exportiert für Tests)
// ---------------------------------------------------------------------------

export const DROP_CM_MIN = 10;
export const DROP_CM_MAX = 500;
export const DEFAULT_DROP_CM = 100;
export const DEFAULT_DIRECTION_DEG = 180;

const COMPASS_LABELS_8 = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'];
const PLAN_LABELS_8 = [
  'oben', 'oben rechts', 'rechts', 'unten rechts',
  'unten', 'unten links', 'links', 'oben links',
];
const PLAN_ARROWS_8 = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];

/** Gefälle-Wert auf den gültigen Bereich klemmen (10–500 cm, 10er-Schritte). */
export function clampDropCm(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return DEFAULT_DROP_CM;
  const clamped = Math.min(DROP_CM_MAX, Math.max(DROP_CM_MIN, num));
  return Math.round(clamped / 10) * 10;
}

/** Anzeige: unter 1 m in cm („80 cm"), ab 1 m in Metern („1,2 m", „2 m"). */
export function formatDropLabel(dropCm) {
  if (dropCm < 100) return `${dropCm} cm`;
  const meters = dropCm / 100;
  const text = Number.isInteger(meters)
    ? String(meters)
    : meters.toFixed(1).replace('.', ',');
  return `${text} m`;
}

/**
 * Richtungs-Label für einen Canvas-Winkel: Himmelsrichtung („SO") wenn
 * northAngle gesetzt ist, sonst plan-relativ („unten rechts").
 */
export function slopeDirectionLabel(directionDeg, northAngle) {
  if (northAngle == null) {
    const idx = Math.round(normalizeAngle(directionDeg) / 45) % 8;
    return PLAN_LABELS_8[idx];
  }
  const bearing = normalizeAngle(directionDeg - northAngle);
  const idx = Math.round(bearing / 45) % 8;
  return COMPASS_LABELS_8[idx];
}

/** Satzbaustein „Gefälle 1,2 m nach SO" (Editor-Indikator + Zusammenfassung). */
export function formatSlopeSummary(baseSlope, northAngle) {
  if (!baseSlope) return null;
  const drop = formatDropLabel(clampDropCm(baseSlope.dropCm));
  const label = slopeDirectionLabel(baseSlope.directionDeg ?? 0, northAngle);
  return `Gefälle ${drop} nach ${label}`;
}

/**
 * 8 Richtungs-Presets. Mit northAngle: Himmelsrichtungen (Canvas-Winkel =
 * northAngle + Kompass-Peilung), sonst plan-relative Pfeile.
 */
export function directionPresets(northAngle) {
  if (northAngle == null) {
    return PLAN_LABELS_8.map((title, i) => ({
      label: PLAN_ARROWS_8[i],
      title,
      angle: i * 45,
    }));
  }
  return COMPASS_LABELS_8.map((label, i) => ({
    label,
    title: label,
    angle: normalizeAngle(northAngle + i * 45),
  }));
}

/**
 * Outline in eine SVG-Vorschau einpassen. Liefert Polygon-Punkte + Zentrum.
 * Fallback ohne gezeichnete Outline (Mobile-Rechteck oder gar nichts):
 * Rechteck im Seitenverhältnis von outline.rect bzw. quadratisch.
 */
export function outlinePreviewGeometry(outline, width, height, padding = 12) {
  const innerW = width - 2 * padding;
  const innerH = height - 2 * padding;

  const vertices = outline?.vertices;
  if (Array.isArray(vertices) && vertices.length >= 3) {
    const xs = vertices.map((v) => v.x);
    const ys = vertices.map((v) => v.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const spanX = maxX - minX || 1;
    const spanY = maxY - minY || 1;
    const scale = Math.min(innerW / spanX, innerH / spanY);
    const offsetX = padding + (innerW - spanX * scale) / 2;
    const offsetY = padding + (innerH - spanY * scale) / 2;
    const points = vertices
      .map((v) => `${offsetX + (v.x - minX) * scale},${offsetY + (v.y - minY) * scale}`)
      .join(' ');
    return { points, cx: width / 2, cy: height / 2 };
  }

  // Rechteck-Fallback (Mobile: outline.rect in Metern) — nur Seitenverhältnis
  const rectW = outline?.rect?.width || 1;
  const rectD = outline?.rect?.depth || 1;
  const scale = Math.min(innerW / rectW, innerH / rectD);
  const w = rectW * scale;
  const h = rectD * scale;
  const x0 = (width - w) / 2;
  const y0 = (height - h) / 2;
  const points = `${x0},${y0} ${x0 + w},${y0} ${x0 + w},${y0 + h} ${x0},${y0 + h}`;
  return { points, cx: width / 2, cy: height / 2 };
}

// ---------------------------------------------------------------------------
// Komponente
// ---------------------------------------------------------------------------

const PREVIEW_W = 200;
const PREVIEW_H = 140;
const ARROW_R = 44;

const BaseSlopeControls = ({
  baseSlope,
  onChange,
  northAngle = null,
  outline = null,
  defaultDropCm = DEFAULT_DROP_CM,
  idPrefix = 'base-slope',
}) => {
  const hasSlope = baseSlope != null;
  const directionDeg = baseSlope?.directionDeg ?? DEFAULT_DIRECTION_DEG;
  const dropCm = clampDropCm(baseSlope?.dropCm ?? defaultDropCm);

  const enableSlope = useCallback(() => {
    onChange({
      directionDeg: DEFAULT_DIRECTION_DEG,
      dropCm: clampDropCm(defaultDropCm),
    });
  }, [onChange, defaultDropCm]);

  const setDirection = useCallback(
    (angle) => {
      onChange({ directionDeg: normalizeAngle(angle), dropCm });
    },
    [onChange, dropCm]
  );

  const handlePreviewClick = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setDirection(snapAngle(angleFromPointerOnCircle(cx, cy, e.clientX, e.clientY)));
    },
    [setDirection]
  );

  const handleDropChange = useCallback(
    (e) => {
      onChange({ directionDeg, dropCm: clampDropCm(e.target.value) });
    },
    [onChange, directionDeg]
  );

  // Pfeil durch das Vorschau-Zentrum: Fuß bergauf, Spitze bergab
  const rad = (directionDeg * Math.PI) / 180;
  const dirX = Math.sin(rad);
  const dirY = -Math.cos(rad);
  const tip = { x: PREVIEW_W / 2 + dirX * ARROW_R, y: PREVIEW_H / 2 + dirY * ARROW_R };
  const tail = { x: PREVIEW_W / 2 - dirX * ARROW_R, y: PREVIEW_H / 2 - dirY * ARROW_R };
  const headLeftRad = rad + (150 * Math.PI) / 180;
  const headRightRad = rad - (150 * Math.PI) / 180;
  const headLen = 10;

  const { points } = outlinePreviewGeometry(outline, PREVIEW_W, PREVIEW_H);
  const presets = directionPresets(northAngle);

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3" data-testid="base-slope-controls">
      <p className="text-base font-bold text-gray-900">
        Ist dein Garten insgesamt geneigt?
      </p>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
          <input
            type="radio"
            name={`${idPrefix}-enabled`}
            checked={!hasSlope}
            onChange={() => onChange(null)}
            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
            data-testid={`${idPrefix}-radio-flat`}
          />
          <span className="text-sm font-medium text-gray-700">Nein, eben</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
          <input
            type="radio"
            name={`${idPrefix}-enabled`}
            checked={hasSlope}
            onChange={enableSlope}
            className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
            data-testid={`${idPrefix}-radio-sloped`}
          />
          <span className="text-sm font-medium text-gray-700">Ja, geneigt</span>
        </label>
      </div>

      {hasSlope && (
        <div className="space-y-4">
          {/* Richtung: Mini-Outline-Vorschau mit Gefälle-Pfeil */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              In welche Richtung fällt das Gelände ab?
            </p>
            <svg
              width={PREVIEW_W}
              height={PREVIEW_H}
              onClick={handlePreviewClick}
              className="block mx-auto bg-white border border-gray-200 rounded-lg cursor-crosshair"
              role="img"
              aria-label={`Gefällerichtung: ${slopeDirectionLabel(directionDeg, northAngle)}. Klicken, um die Richtung zu ändern.`}
              data-testid={`${idPrefix}-preview`}
            >
              <polygon points={points} fill="#ECFDF5" stroke="#10B981" strokeWidth="1.5" strokeDasharray="4 3" />
              <line x1={tail.x} y1={tail.y} x2={tip.x} y2={tip.y} stroke="#059669" strokeWidth="2.5" strokeLinecap="round" />
              <line
                x1={tip.x}
                y1={tip.y}
                x2={tip.x + headLen * Math.sin(headLeftRad)}
                y2={tip.y - headLen * Math.cos(headLeftRad)}
                stroke="#059669"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <line
                x1={tip.x}
                y1={tip.y}
                x2={tip.x + headLen * Math.sin(headRightRad)}
                y2={tip.y - headLen * Math.cos(headRightRad)}
                stroke="#059669"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <text
                x={tail.x - dirX * 12}
                y={tail.y - dirY * 12}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="9"
                fill="#6B7280"
              >
                hoch
              </text>
              <text
                x={tip.x + dirX * 14}
                y={tip.y + dirY * 14}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="9"
                fill="#6B7280"
              >
                tief
              </text>
            </svg>
            <div className="grid grid-cols-4 gap-1.5 mt-2">
              {presets.map(({ label, title, angle }) => {
                const isActive = normalizeAngle(directionDeg) === angle;
                return (
                  <button
                    key={`${label}-${angle}`}
                    type="button"
                    onClick={() => setDirection(angle)}
                    title={title}
                    aria-pressed={isActive}
                    className={`min-h-[44px] px-2 text-sm rounded-md border transition-colors ${
                      isActive
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {northAngle == null && (
              <p className="text-xs text-gray-500 mt-1.5">
                Richtungen beziehen sich auf deinen Plan. Die Nordrichtung kannst du
                später im Editor festlegen.
              </p>
            )}
          </div>

          {/* Stärke */}
          <div className="space-y-2">
            <label
              htmlFor={`${idPrefix}-drop`}
              className="block text-sm font-medium text-gray-700"
            >
              Höhenunterschied insgesamt:{' '}
              <span className="font-bold text-emerald-600">{formatDropLabel(dropCm)}</span>
            </label>
            <input
              id={`${idPrefix}-drop`}
              type="range"
              min={DROP_CM_MIN}
              max={DROP_CM_MAX}
              step={10}
              value={dropCm}
              onChange={handleDropChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              data-testid={`${idPrefix}-drop-slider`}
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>10 cm</span>
              <span>5 m</span>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-sm text-emerald-800" data-testid={`${idPrefix}-summary`}>
              {formatSlopeSummary({ directionDeg, dropCm }, northAngle)}. Sichtbar wird
              das Gefälle in der 3D-Ansicht.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseSlopeControls;
